<script setup lang="ts">
import {
    BusinessEntity,
    BusinessEntityFieldDefinition,
    BusinessEntityFieldType,
    FieldType,
    useBusinessEntitiesStore,
} from "@/stores/business-entity-store";
import { storeToRefs } from "pinia";
import { DataTableRowEditSaveEvent } from "primevue/datatable";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import { Ref, inject, onMounted, ref } from "vue";
import InputText from "primevue/inputtext";
import { useHotKeys } from "@/composables/hotkeys";

const { createEntity, updateEntity } = useBusinessEntitiesStore();
const { statusCode, isLoading } = storeToRefs(useBusinessEntitiesStore());
const { addHotKey } = useHotKeys();

const editEntity = ref<BusinessEntity | undefined>();
const editingRows = ref<FieldType[]>([]);
const newName = ref("");
const newDescription = ref<string | undefined>("");
const fields = ref<FieldType[]>([]);

const fieldTypes = Object.entries(BusinessEntityFieldType).map(
    ([label, value]) => ({ label, value })
);

const dialogRef = inject<Ref<DynamicDialogInstance>>("dialogRef");
const cancel = () => dialogRef?.value.close();

/**
 * Save on cmd/ctrl + enter.
 */
addHotKey("CommandOrMeta+Enter", async () => {
    await closeAndSave();
});

const fieldsToDefinition = (
    fields: FieldType[]
): BusinessEntityFieldDefinition[] => {
    return fields
        .map((field) => {
            return {
                name: field.name,
                type: field.type,
                description: field.description,
            };
        })
        .filter((field) => field.name != ""); // omit the edit row
};

const definitionToFields = (
    definition: BusinessEntityFieldDefinition[]
): FieldType[] => {
    return definition.map((field, index) => {
        return {
            id: index,
            name: field.name,
            type: field.type,
            description: field.description,
        };
    });
};

const closeAndSave = async () => {
    // remove the editing fields row (last entry)
    fields.value.pop();
    if (editEntity.value) {
        await updateEntity({
            identity: editEntity.value.identity,
            domain_id: editEntity.value.domain_id,
            metadata: {
                name: newName.value,
                description: newDescription.value,
            },
            definition: fieldsToDefinition(fields.value),
        });
    } else {
        await createEntity(newName.value, newDescription.value, fields.value);
    }
    if (statusCode.value && statusCode.value < 300) {
        dialogRef?.value?.close();
    }
};

onMounted(() => {
    editEntity.value = dialogRef?.value?.data;
    if (editEntity.value) {
        newName.value = editEntity.value.metadata.name;
        newDescription.value =
            editEntity.value.metadata.description ?? undefined;
        fields.value = definitionToFields(editEntity.value.definition || []);
    }
    addEditField();
});

// we want a maximum of two rows to be in editMode (so the now editing rows are clickable and usable for navigation)
// 1. the new (last) row thats presented for creating a new field
// 2. one of the existing entities
const preventTripleEdit = () => {
    if (editingRows.value.length > 2) {
        editingRows.value.pop();
    }
};

function addEditField() {
    const editField = {
        id: fields.value?.length ?? 0,
        type: BusinessEntityFieldType.STRING,
        name: "",
        description: "",
    };
    fields.value.push(editField);
    editingRows.value = [editField];
}

const onRowEditSave = (event: DataTableRowEditSaveEvent) => {
    // TODO check for duplicates
    // indicate some error about the name prop being required
    if (event.newData.name === "") {
        editingRows.value = [
            {
                id: event.index,
                type: BusinessEntityFieldType.STRING,
                name: "",
                description: "",
            },
        ];
        return;
    }

    // Always update the field, regardless of whether it's the last row or not
    fields.value[event.index] = {
        ...event.newData,
        id: event.index,
    };

    // If it's the last row (edit row), add a new edit field
    if (event.index === fields.value.length - 1) {
        addEditField();
    }
};

const removeField = (index: number) => {
    fields.value.splice(index, 1);
    // reflect the change of index to the editRow
    editingRows.value = [
        {
            id: fields.value.length - 1,
            type: BusinessEntityFieldType.STRING,
            name: "",
            description: "",
        },
    ];
};
</script>

<template>
    <BlockUI :blocked="isLoading">
        <Fluid class="field">
            <label for="name">Name</label>
            <InputText
                id="name"
                autofocus
                v-model="newName"
                required
                :value="newName"
                style="width: 100%"
            />
        </Fluid>
        <Fluid class="field">
            <label for="description">Description</label>
            <InputText
                id="description"
                v-model="newDescription"
                style="width: 100%"
            />
        </Fluid>

        <DataTable
            v-model:editingRows="editingRows"
            :value="fields"
            class="field"
            editMode="row"
            dataKey="id"
            @row-edit-init="preventTripleEdit"
            @row-edit-save="onRowEditSave"
        >
            <Column field="type" header="Type">
                <template #editor="{ data }">
                    <Select
                        v-model="data['type']"
                        :options="fieldTypes"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="field type"
                        fluid
                    >
                    </Select>
                    <!-- <InputText v-model="data['type']" /> -->
                </template>
            </Column>
            <Column field="name" header="Name" style="">
                <template #editor="{ data }">
                    <InputText v-model="data['name']" />
                </template>
            </Column>
            <Column field="description" header="Description" style="">
                <template #editor="{ data }">
                    <InputText v-model="data['description']" fluid />
                </template>
            </Column>
            <Column :rowEditor="true" />
            <Column field="">
                <template #editor="{ index }">
                    <Button
                        v-if="index !== fields.length - 1"
                        type="button"
                        text
                        rounded
                        icon="pi pi-trash"
                        @click="removeField(index)"
                    />
                </template>
            </Column>
        </DataTable>

        <div class="buttons">
            <Button label="Cancel" text severity="secondary" @click="cancel" />
            <Button label="Save" severity="primary" @click="closeAndSave" />
        </div>
    </BlockUI>
</template>

<style lang="scss" scoped>
.field {
    padding-bottom: 1em;

    label {
        display: block;
        margin-bottom: 0.5em;
    }
}

.buttons {
    display: flex;
    justify-content: end;
    margin-top: 1em;
    gap: 1em;
}
</style>
